import { useEffect, useRef, useState } from "react";
import classes from "../homePageNew.module.css";
import { ChevronDownIcon, CheckIcon } from "./icons";

/**
 * One single-value dropdown, styled like CombinationSelector's own
 * trigger + menu — kept separate from CombinationSelector since
 * Monitoring's selectors need cascading narrowing across several
 * dimensions, which doesn't fit that component's "every dimension's
 * options come from one flat combinations list" model. Shared by the
 * Combination bar and the chart's own LLM picker so both look identical.
 * `alignRight` opens the menu flush to the field's right edge, for fields
 * that sit against the right side of a card.
 */
export default function MonitoringSelectField({ label, value, valueLabel, options, onChange, disabled, alignRight }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  if (disabled || options.length <= 1) {
    return (
      <div className={classes.comboField}>
        <span className={classes.comboLabel}>{label}</span>
        <span className={classes.comboStatic} title={valueLabel}>
          {valueLabel || "—"}
        </span>
      </div>
    );
  }

  return (
    <div className={classes.comboField} ref={rootRef}>
      <span className={classes.comboLabel}>{label}</span>
      <button type="button" className={`${classes.comboTrigger} ${open ? classes.comboTriggerOpen : ""}`} onClick={() => setOpen((v) => !v)} title={valueLabel}>
        <span className={classes.comboTriggerValue}>{valueLabel}</span>
        <span className={`${classes.comboChevron} ${open ? classes.comboChevronOpen : ""}`}>
          <ChevronDownIcon size={12} />
        </span>
      </button>
      {open && (
        <div className={`${classes.comboMenu} ${alignRight ? classes.comboMenuRight : ""}`} role="listbox">
          {options.map((opt) => {
            const active = opt.id === value;
            return (
              <button
                type="button"
                key={opt.id}
                role="option"
                aria-selected={active}
                className={`${classes.comboMenuItem} ${active ? classes.comboMenuItemActive : ""}`}
                onClick={() => {
                  onChange(opt.id);
                  setOpen(false);
                }}
              >
                <span>{opt.name}</span>
                {active && <CheckIcon size={13} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
